import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import StatusPill from '../../components/ui/StatusPill';
import { getEmailTemplateList } from '../../actions/emailTemplate';
import { getWhatsAppTemplateList } from '../../actions/whatsappNotification';
import './admin.css';

/** Template endpoints return the full set; filter and page client-side. */
function makeFetcher(loader, searchKeys) {
  return async (params) => {
    let items = (await loader()) || [];
    if (params.search) {
      const q = params.search.toLowerCase();
      items = items.filter((t) =>
        searchKeys
          .map((k) => t[k])
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    const total = items.length;
    const start = (params.page - 1) * params.limit;
    return { items: items.slice(start, start + params.limit), total };
  };
}

/**
 * Email and WhatsApp templates share a screen shell; `channel` decides the columns,
 * the loader and where a row navigates to.
 */
export default function TemplateList({ channel = 'email' }) {
  const navigate = useNavigate();
  const isEmail = channel === 'email';

  const fetcher = useMemo(
    () =>
      isEmail
        ? makeFetcher(getEmailTemplateList, ['template_alias', 'subject', 'sender_name', 'description'])
        : makeFetcher(getWhatsAppTemplateList, ['template_alias', 'category', 'description']),
    [isEmail]
  );

  const list = useListState({ views: [], fetcher, limit: 20 });

  const columns = useMemo(
    () =>
      isEmail
        ? [
            {
              key: 'template_alias',
              header: 'Template',
              width: 220,
              mono: true,
              render: (r) => r.template_alias,
            },
            { key: 'subject', header: 'Subject', width: 300, render: (r) => r.subject || '—' },
            {
              key: 'sender',
              header: 'Sender',
              width: 230,
              render: (r) => (
                <span>
                  <span style={{ display: 'block' }}>{r.sender_name || '—'}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>{r.sender_id}</span>
                </span>
              ),
            },
            {
              key: 'description',
              header: 'When it sends',
              muted: true,
              render: (r) => r.description || '—',
            },
          ]
        : [
            {
              key: 'category',
              header: 'Category',
              width: 150,
              render: (r) => <StatusPill tone="info">{r.category || 'Other'}</StatusPill>,
            },
            { key: 'content_type', header: 'Content type', width: 160, muted: true, render: (r) => r.content_type || '—' },
            { key: 'template_alias', header: 'Alias', width: 240, mono: true },
            { key: 'description', header: 'Description', muted: true, render: (r) => r.description || '—' },
          ],
    [isEmail]
  );

  const open = useCallback(
    (row) => navigate(`${isEmail ? '/email-template' : '/whatsapp-template'}/${row.template_id}`),
    [navigate, isEmail]
  );

  return (
    <>
      <PageHeader
        section="Messaging"
        title={isEmail ? 'Email templates' : 'WhatsApp templates'}
        subtitle={
          isEmail
            ? 'Transactional emails the platform sends, and who they go to.'
            : 'Approved WhatsApp templates available for bulk sends.'
        }
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.template_id}
        noun="templates"
        selectable={false}
        searchPlaceholder={isEmail ? 'Alias or subject' : 'Alias or category'}
        onRowClick={open}
        empty={{
          icon: isEmail ? 'bx-envelope' : 'bx-message-rounded',
          title: 'No templates',
          description: 'Templates are managed in the provider and synced here.',
        }}
      />
    </>
  );
}
